/**
 * stockSettings — quando o app mexe no estoque sozinho.
 *
 * ─── A REGRA QUE NÃO PODE SER QUEBRADA ──────────────────────────────────────
 *
 * **Os padrões reproduzem EXATAMENTE o comportamento anterior à feature.**
 * Antes disto o comportamento era fixo em código: prompt manual ao aprovar
 * orçamento e ao concluir OS, automático só no PDV, e nada nos demais casos.
 * Ninguém que já usa o app pode acordar com o estoque se mexendo sozinho — por
 * isso `DEFAULT_STOCK_SETTINGS` é "perguntar" nos dois gatilhos que existiam e
 * DESLIGADO nos três que não existiam.
 *
 * Consequência prática: uma empresa sem linha em `stock_settings` (todas, hoje)
 * se comporta como antes. A linha só nasce quando o usuário muda algo.
 *
 * ─── QUANDO É SEGURO BAIXAR: ENTREGA, NÃO ACEITE ────────────────────────────
 *
 * O gatilho de orçamento é `quote_completed` e dispara na CONCLUSÃO, não na
 * aprovação. Orçamento aprovado é **intenção do cliente**: ele pode desistir, o
 * serviço pode não acontecer, o material pode nunca sair da prateleira. Baixar
 * ali reserva mercadoria por causa de um "pode ser". A régua é a mesma que o DRE
 * já aplica ao custo — o documento que representa a ENTREGA:
 *
 *  - com OS no fluxo: a entrega é a **OS concluída** (`service_order_completed`);
 *  - sem OS (a maioria dos prestadores): a entrega é o **orçamento concluído**;
 *  - PDV: entrega imediata, baixa na hora e sem perguntar (fora deste módulo).
 *
 * ⚠️ Os dois primeiros descrevem a MESMA venda quando a OS nasce de um
 * orçamento. Quem impede a baixa dobrada é o guard de CADEIA do
 * `StockDeductionPrompt`, não este módulo: aqui os dois gatilhos são
 * independentes de propósito (uma OS avulsa não tem orçamento nenhum).
 *
 * ─── OS CINCO GATILHOS TÊM RUNTIME ──────────────────────────────────────────
 *
 * `document_deleted_return` e `quantity_changed` nasceram modelados e inertes
 * (a tabela tinha as colunas, a tela dizia "Em breve"). Ganharam runtime no
 * grupo 10 e saíram de `UNIMPLEMENTED_TRIGGERS`. Os padrões NÃO mudaram: os
 * dois continuam nascendo desligados, então nada muda para quem já usa o app.
 *
 * ─── O PDV NÃO ESTÁ AQUI, DE PROPÓSITO ──────────────────────────────────────
 *
 * A Venda Rápida baixa o estoque na hora, sem perguntar, porque é uma venda de
 * balcão já consumada — o produto saiu da prateleira antes de o app saber. Um
 * "perguntar" ali seria um diálogo entre o cliente e a maquininha. Se um dia
 * virar configurável, é decisão de produto, não efeito colateral deste módulo.
 */

/** `ask` = mostrar o convite e esperar confirmação. `auto` = executar direto. */
export type StockTriggerMode = 'ask' | 'auto';
/** NULL = herdar da empresa. Ausência é um terceiro estado, não `off`. */
export type ProductStockDeductionMode = 'off' | StockTriggerMode | null;

export type StockTriggerKey =
  | 'quote_completed'
  | 'service_order_completed'
  | 'quote_cancelled_return'
  | 'document_deleted_return'
  | 'quantity_changed';

export interface StockTriggerConfig {
  enabled: boolean;
  mode: StockTriggerMode;
}

export type StockSettings = Record<StockTriggerKey, StockTriggerConfig>;

/**
 * O estado que reproduz o app antes desta feature. **Não mude sem migrar os
 * usuários**: mudar um `false` para `true` aqui liga movimentação automática de
 * estoque para todo mundo de uma vez.
 */
export const DEFAULT_STOCK_SETTINGS: StockSettings = {
  quote_completed: { enabled: true, mode: 'ask' },
  service_order_completed: { enabled: true, mode: 'ask' },
  quote_cancelled_return: { enabled: false, mode: 'ask' },
  document_deleted_return: { enabled: false, mode: 'ask' },
  quantity_changed: { enabled: false, mode: 'ask' },
};

/**
 * Gatilhos ainda NÃO implementados no runtime — **hoje, nenhum**.
 *
 * O mecanismo continua aqui de propósito: ele é o que garante que um gatilho
 * modelado no banco mas sem runtime nunca aja, mesmo ligado à força por SQL.
 * Um interruptor que não faz nada é pior do que interruptor nenhum — o usuário
 * liga, confia, e o estoque não volta.
 *
 * ⚠️ A LISTA E O RUNTIME ANDAM JUNTOS, NOS DOIS SENTIDOS:
 *  - implementar o comportamento e ESQUECER de tirar a chave daqui deixa a
 *    feature inerte e sem erro nenhum — `triggerAction` continua devolvendo
 *    'off' e o interruptor da tela nem aparece habilitado;
 *  - tirar a chave daqui sem o runtime faz o contrário, que é pior: o usuário
 *    liga e o estoque não se mexe.
 * Os testes de `stockSettings.test.ts` cobram os dois lados.
 *
 * `document_deleted_return` e `quantity_changed` saíram desta lista quando
 * ganharam runtime (ver `stockDocumentReturn.ts` e `quantityAdjustmentLines`
 * em `stockReturn.ts`).
 */
export const UNIMPLEMENTED_TRIGGERS: readonly StockTriggerKey[] = [];

/**
 * As quatro opções do seletor "Baixa deste produto", num lugar só — o campo
 * existe no formulário do catálogo E no card de item do orçamento (que cadastra
 * o mesmo produto). `inherit` é o rótulo do `null`: ausência é "herdar da
 * empresa", nunca "não dar baixa" (#97).
 */
export const PRODUCT_STOCK_MODE_INHERIT = 'inherit';

export const PRODUCT_STOCK_MODE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: PRODUCT_STOCK_MODE_INHERIT, label: 'Herdar da empresa' },
  { value: 'off', label: 'Não dar baixa' },
  { value: 'ask', label: 'Perguntar antes' },
  { value: 'auto', label: 'Dar baixa automaticamente' },
];

/** Valor do seletor → coluna. Só o vocabulário fechado passa; o resto é `null`. */
export function productStockModeFromSelect(value: string): ProductStockDeductionMode {
  return value === 'off' || value === 'ask' || value === 'auto' ? value : null;
}

export const STOCK_TRIGGERS = [
  {
    key: 'quote_completed',
    label: 'Orçamento concluído',
    description:
      'Dar baixa nos produtos quando o orçamento for marcado como concluído — o passo que representa a entrega. Aprovar não baixa: é só o aceite do cliente.',
  },
  {
    key: 'service_order_completed',
    label: 'Ordem de serviço concluída',
    description: 'Dar baixa nos produtos quando a OS for marcada como concluída.',
  },
  {
    key: 'quote_cancelled_return',
    label: 'Orçamento cancelado',
    description: 'Devolver ao estoque o que já tinha sido baixado, se o orçamento for cancelado ou rejeitado.',
  },
  {
    key: 'document_deleted_return',
    label: 'Documento excluído',
    description:
      'Devolver ao estoque quando o orçamento ou a OS for excluído. A escolha aparece no aviso de confirmação da exclusão.',
  },
  {
    key: 'quantity_changed',
    label: 'Quantidade alterada',
    description:
      'Acertar o estoque quando a quantidade de um item mudar depois da baixa. Item novo continua sendo tratado pelo convite de baixa.',
  },
] as const satisfies readonly { key: StockTriggerKey; label: string; description: string }[];

/** A coluna de cada gatilho na tabela — usada pelo leitor e pelo gravador. */
const COLUMNS: Record<StockTriggerKey, { enabled: string; mode: string }> = {
  quote_completed: { enabled: 'quote_completed_enabled', mode: 'quote_completed_mode' },
  service_order_completed: {
    enabled: 'service_order_completed_enabled',
    mode: 'service_order_completed_mode',
  },
  quote_cancelled_return: {
    enabled: 'quote_cancelled_return_enabled',
    mode: 'quote_cancelled_return_mode',
  },
  document_deleted_return: {
    enabled: 'document_deleted_return_enabled',
    mode: 'document_deleted_return_mode',
  },
  quantity_changed: { enabled: 'quantity_changed_enabled', mode: 'quantity_changed_mode' },
};

function toMode(value: unknown, fallback: StockTriggerMode): StockTriggerMode {
  return value === 'auto' || value === 'ask' ? value : fallback;
}

/**
 * Lê a linha do banco (ou o cache) para o formato da aplicação.
 *
 * Linha AUSENTE → padrões. Coluna ausente/nula numa linha existente → o padrão
 * daquele gatilho, nunca `false`: `Boolean(null) === false` desligaria a baixa
 * por orçamento em silêncio para quem já usa (mesmo modo de falha do #61 e do
 * #52 — ausência não é zero, e aqui ausência não é "desligado").
 */
export function stockSettingsFromRow(row: Record<string, unknown> | null | undefined): StockSettings {
  const out = {} as StockSettings;
  for (const key of Object.keys(COLUMNS) as StockTriggerKey[]) {
    const fallback = DEFAULT_STOCK_SETTINGS[key];
    const rawEnabled = row?.[COLUMNS[key].enabled];
    out[key] = {
      enabled: typeof rawEnabled === 'boolean' ? rawEnabled : fallback.enabled,
      mode: toMode(row?.[COLUMNS[key].mode], fallback.mode),
    };
  }
  return out;
}

/** Monta as 10 colunas de uma vez — nenhum caminho as enumera à mão (#33/#62). */
export function toStockSettingsColumns(settings: StockSettings): Record<string, boolean | string> {
  const out: Record<string, boolean | string> = {};
  for (const key of Object.keys(COLUMNS) as StockTriggerKey[]) {
    out[COLUMNS[key].enabled] = settings[key].enabled;
    out[COLUMNS[key].mode] = settings[key].mode;
  }
  return out;
}

/**
 * O que fazer quando o gatilho dispara: nada, perguntar, ou executar.
 *
 * Um gatilho listado em `UNIMPLEMENTED_TRIGGERS` NUNCA age, mesmo que a coluna
 * esteja `true` no banco (alguém pode ter ligado por SQL) — é o que garante que
 * "Em breve" signifique de verdade "ainda não faz nada". Hoje a lista está
 * vazia: os cinco gatilhos têm runtime.
 */
export function triggerAction(
  settings: StockSettings | null | undefined,
  key: StockTriggerKey,
): 'off' | 'ask' | 'auto' {
  if (UNIMPLEMENTED_TRIGGERS.includes(key)) return 'off';
  const config = (settings ?? DEFAULT_STOCK_SETTINGS)[key] ?? DEFAULT_STOCK_SETTINGS[key];
  if (!config.enabled) return 'off';
  return config.mode;
}

/**
 * Precedência única: produto → empresa → padrão. Manter isto puro evita que
 * cada tela interprete `null` de um jeito — em especial, `null` NUNCA desliga.
 */
export function resolveProductStockAction(
  productMode: ProductStockDeductionMode | undefined,
  settings: StockSettings | null | undefined,
  key: StockTriggerKey,
): 'off' | 'ask' | 'auto' {
  if (UNIMPLEMENTED_TRIGGERS.includes(key)) return 'off';
  if (productMode === 'off' || productMode === 'ask' || productMode === 'auto') {
    return productMode;
  }
  return triggerAction(settings, key);
}
